import { useState, useEffect } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Switch } from "@/modern/components/ui/switch";
import { Card } from "@/modern/components/ui/card";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

const ScheduleDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { userPreferences } = useSwivuBryntumContext();

  // State for all settings
  const [selectedRowHighlight, setSelectedRowHighlight] = useState(false);
  const [hoverGridCellHighlight, setHoverGridCellHighlight] = useState(true);
  const [hoverTaskEventHighlight, setHoverTaskEventHighlight] = useState(false);

  // Listen to userPreferences store changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.userPreferencesStore,
    subscriberFn: ({ record }) => {
      const scheduleKeys = [
        PERSISTENCE_STATE_KEYS.selectedRowHighlight,
        PERSISTENCE_STATE_KEYS.hoverGridCellHighlight,
        PERSISTENCE_STATE_KEYS.hoverTaskEventHighlight,
      ];

      if (record?.key && scheduleKeys.includes(record.key)) {
        setInitialSettings();
      }
    }
  });

  useEffect(() => {
    setInitialSettings();
  }, []);

  const setInitialSettings = () => {
    if (userPreferences) {
      // Load all settings with defaults
      const rowHighlight = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.selectedRowHighlight) ?? "false";
      setSelectedRowHighlight(JSON.parse(rowHighlight));

      const gridHighlight = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hoverGridCellHighlight) ?? "true";
      setHoverGridCellHighlight(JSON.parse(gridHighlight));

      const eventHighlight = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hoverTaskEventHighlight) ?? "false";
      setHoverTaskEventHighlight(JSON.parse(eventHighlight));
    }
  };

  // Generic handler for setting changes
  const handleSettingChange = (
    key: string,
    value: boolean | string,
    setter: (value: any) => void
  ) => {
    setter(value);
    userPreferences?.updateSetting(key, String(value));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Schedule Highlighting Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Schedule Highlighting</h3>
              <p className="text-xs text-muted-foreground">Configure visual highlighting options in the schedule view</p>
            </div>

            <div className="space-y-4">
              {/* Selected Row Highlight */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="selected-row-highlight" className="text-sm font-medium">
                    Enable Recently Selected Row Highlighting
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Highlight the most recently selected row in the schedule
                  </p>
                </div>
                <Switch
                  id="selected-row-highlight"
                  checked={selectedRowHighlight}
                  onCheckedChange={(checked) =>
                    handleSettingChange(PERSISTENCE_STATE_KEYS.selectedRowHighlight, checked, setSelectedRowHighlight)
                  }
                />
              </div>

              {/* Hover Grid Cell Highlight */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="hover-grid-cell" className="text-sm font-medium">
                    Enable Grid Cell Hover Highlighting
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Highlight grid cells when hovering over them
                  </p>
                </div>
                <Switch
                  id="hover-grid-cell"
                  checked={hoverGridCellHighlight}
                  onCheckedChange={(checked) =>
                    handleSettingChange(PERSISTENCE_STATE_KEYS.hoverGridCellHighlight, checked, setHoverGridCellHighlight)
                  }
                />
              </div>

              {/* Hover Task Event Highlight */}
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="hover-task-event" className="text-sm font-medium">
                    Enable Task Event Hover Highlighting
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Highlight task events when hovering over them
                  </p>
                </div>
                <Switch
                  id="hover-task-event"
                  checked={hoverTaskEventHighlight}
                  onCheckedChange={(checked) =>
                    handleSettingChange(PERSISTENCE_STATE_KEYS.hoverTaskEventHighlight, checked, setHoverTaskEventHighlight)
                  }
                />
              </div>
            </div>
          </div>
        </Card>

      </div>
    </div>
  );
};

export default ScheduleDetail;
